import { useState } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Share2, 
  Copy, 
  Check,
  MessageCircle,
  Facebook,
  Instagram
} from 'lucide-react';

// Twitter/X icon component
const TwitterIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default function SocialShare({ event, eventTitle, eventUrl }) {
  const [copied, setCopied] = useState(false);

  // Generate share URL - prefer explicit eventUrl, fall back to event object
  const shareUrl = eventUrl 
    || (typeof window !== 'undefined' ? `${window.location.origin}/events/${event?.event_id}` : '');
  
  const title = eventTitle || event?.title || '';
  const shareText = `Check out ${title} on Next Gig!`;
  const shareTextEncoded = encodeURIComponent(shareText);
  const shareUrlEncoded = encodeURIComponent(shareUrl);

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${shareTextEncoded}%20${shareUrlEncoded}`,
    twitter: `https://twitter.com/intent/tweet?text=${shareTextEncoded}&url=${shareUrlEncoded}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrlEncoded}`,
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    } else {
      copyToClipboard();
    }
  };

  const openShareWindow = (url) => {
    window.open(url, '_blank', 'width=600,height=400,scrollbars=yes');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="lg" 
          className="gap-2"
          data-testid="share-event-btn"
        >
          <Share2 className="w-4 h-4" />
          Share Event
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {/* WhatsApp */}
        <DropdownMenuItem 
          onClick={() => openShareWindow(shareLinks.whatsapp)}
          className="cursor-pointer gap-3"
          data-testid="share-whatsapp"
        >
          <div className="p-1.5 rounded-full bg-green-500/10">
            <WhatsAppIcon className="w-4 h-4 text-green-500" />
          </div>
          <span>WhatsApp</span>
        </DropdownMenuItem>

        {/* Twitter/X */}
        <DropdownMenuItem 
          onClick={() => openShareWindow(shareLinks.twitter)}
          className="cursor-pointer gap-3"
          data-testid="share-twitter"
        >
          <div className="p-1.5 rounded-full bg-foreground/10">
            <TwitterIcon className="w-4 h-4" />
          </div>
          <span>Twitter / X</span>
        </DropdownMenuItem>

        {/* Facebook */}
        <DropdownMenuItem 
          onClick={() => openShareWindow(shareLinks.facebook)}
          className="cursor-pointer gap-3"
          data-testid="share-facebook"
        >
          <div className="p-1.5 rounded-full bg-blue-600/10">
            <Facebook className="w-4 h-4 text-blue-600" />
          </div>
          <span>Facebook</span>
        </DropdownMenuItem>

        {/* Instagram (Copy link) */}
        <DropdownMenuItem 
          onClick={copyToClipboard}
          className="cursor-pointer gap-3"
          data-testid="share-instagram"
        >
          <div className="p-1.5 rounded-full bg-gradient-to-br from-purple-500/10 to-pink-500/10">
            <Instagram className="w-4 h-4 text-pink-500" />
          </div>
          <span>Instagram (Copy Link)</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Copy Link */}
        <DropdownMenuItem 
          onClick={copyToClipboard}
          className="cursor-pointer gap-3"
          data-testid="share-copy-link"
        >
          <div className="p-1.5 rounded-full bg-muted">
            {copied ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </div>
          <span>{copied ? 'Copied!' : 'Copy Link'}</span>
        </DropdownMenuItem>

        {/* Native Share (Mobile) */}
        {typeof navigator !== 'undefined' && navigator.share && (
          <DropdownMenuItem 
            onClick={handleNativeShare}
            className="cursor-pointer gap-3"
            data-testid="share-native"
          >
            <div className="p-1.5 rounded-full bg-primary/10">
              <Share2 className="w-4 h-4 text-primary" />
            </div>
            <span>More Options</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
